import { useEffect } from "react";
import { connect } from "react-redux";
import { Formik } from "formik";
import { Container, Typography, Box, Grid, MenuItem, Paper } from "@material-ui/core";
import Button from "@material-ui/core/Button";

import { isAdmin } from "../../utils/constants";
import PageHero from "../shared/page-hero";
import { TicketSoilFLOViewProps } from "../../interface/TicketSoilFLOViewProps";
import { TicketSoilFLOFormValues } from "../../interface/formik/TicketSoilFLOFormValues";
import FormTextField from "../shared/form-text-field";
import FormSelect from "../shared/form-select";
import SharedButton from "../shared/Button";
import { SoilFLOIcon, StatusIconGroup, ReceivedIcon, NotCompleteIcon } from "../shared/icons";
import FormDateTimePicker from "../shared/form-date-time-picker";
import { useAppSelector, useAppDispatch } from "../../hooks";
import { User } from "../../interface/api/User";
import { SetUsersAction } from "../../interface/redux-types/SetUsersAction";
import userService from "../../services/userService";
import { getUsers, setUsers } from "../../store/userStore";
import FormErrorMessage from "../shared/form-error-message";
import { useSoilFLOStyles } from "../../utils/soilflo-styles";

const TicketSoilFLOView = ({
  ticketFormValues,
  onReceiveSoilFLOTicket,
  user,
  onSaveSoilFLOTicket,
  saving,
  load,
  setRejectModalOpen,
}: TicketSoilFLOViewProps) => {
  const classes = useSoilFLOStyles();
  const dispatch = useAppDispatch();
  const users = useAppSelector(getUsers);

  useEffect(() => {
    userService.getUsers().then((response) => {
      if (response.ok) {
        const users = response.data as User[];
        const setUsersAction: SetUsersAction = { users };
        dispatch(setUsers(setUsersAction));
      }
    });
  }, []);

  const initialValues: TicketSoilFLOFormValues = {
    id: ticketFormValues.id,
    ticketNumber: ticketFormValues.ticketNumber,
    soilFLOData: { ...load },
    date: new Date(ticketFormValues.date || ""),
    userFirstName: "",
    userLastName: "",
    userPhoneNumber: "",
    timeReceived: ticketFormValues.timeReceived,
    isReceived: ticketFormValues.isReceived,
    userId: ticketFormValues.userId,
  };

  return (
    <Container maxWidth="sm">
      <PageHero title="" subtitle="" showBackButton />
      {/* Status icons and action buttons */}
      <Box display="flex" alignItems="center" mt={2} mb={1}>
        <StatusIconGroup>
          {initialValues.isReceived && <ReceivedIcon />}
          <SoilFLOIcon />
        </StatusIconGroup>
        <Box mr={2} />
        <Button
          variant="outlined"
          color="secondary"
          size="small"
          onClick={() => setRejectModalOpen(true)}
        >
          Reject
        </Button>
      </Box>

      <Paper style={{ padding: 24, marginTop: 16 }}>
        <Formik
          initialValues={initialValues}
          onSubmit={(values) => onReceiveSoilFLOTicket(values)}
          enableReinitialize
        >
          {({ values, handleSubmit, setFieldValue }) => (
            <form onSubmit={handleSubmit}>
              {/* SoilFLO Load Info */}
              <Box mb={1}>
                <Typography variant="h6">
                  SoilFLO Load <SoilFLOIcon />
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" mb={2} style={{ gap: 8 }}>
                <Typography variant="body2" color="textSecondary">
                  Fields pulled from SoilFLO are read-only
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <div className={classes.soilFLOField}>
                    <FormTextField
                      name="id"
                      label="Ticket Id"
                      value={values.id}
                    />
                  </div>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <div className={classes.soilFLOField}>
                    <FormTextField
                      name="ticketNumber"
                      label="Ticket Number"
                      value={values.ticketNumber}
                    />
                  </div>
                </Grid>
              </Grid>

              <div style={{ border: "1px solid #ccc", marginTop: 16, marginBottom: 16 }} />

              {/* Loading Site Info */}
              <Typography variant="h6" gutterBottom>
                Loading Site Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <div className={classes.soilFLOField}>
                    <FormTextField
                      label="Job Site"
                      value={values?.soilFLOData?.sourceSite?.name}
                    />
                  </div>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <div className={classes.soilFLOField}>
                    <FormTextField
                      label="Contractor"
                      value={
                        values?.soilFLOData?.sourceSite?.contractingcompany?.name
                      }
                    />
                  </div>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <div className={classes.soilFLOField}>
                    <FormTextField
                      label="Site Contact Name"
                      value={
                        values?.soilFLOData?.assignerUser
                          ? `${values?.soilFLOData?.assignerUser?.firstName} ${values?.soilFLOData?.assignerUser?.lastName} [${values?.soilFLOData?.assignerUser?.phone}]`
                          : ""
                      }
                    />
                  </div>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <div className={classes.soilFLOField}>
                    <FormDateTimePicker
                      label="Time Loaded"
                      value={
                        values?.soilFLOData?.dispatchedTime
                          ? new Date(values?.soilFLOData?.dispatchedTime)
                          : null
                      }
                      readOnly
                      onChange={undefined}
                    />
                  </div>
                </Grid>
              </Grid>

              <div style={{ border: "1px solid #ccc", marginTop: 16, marginBottom: 16 }} />

              {/* Transport Info */}
              <Typography variant="h6" gutterBottom>
                Transport Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <div className={classes.soilFLOField}>
                    <FormTextField
                      label="Haulage Co."
                      value={
                        values?.soilFLOData?.truck?.global_truck?.hauler?.name
                      }
                    />
                  </div>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <div className={classes.soilFLOField}>
                    <FormTextField
                      label="Truck"
                      value={values?.soilFLOData?.truck?.global_truck?.number}
                    />
                  </div>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <div className={classes.soilFLOField}>
                    <FormTextField
                      label="Truck Driver Name"
                      value={values?.soilFLOData?.truck?.driverName}
                    />
                  </div>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <div className={classes.soilFLOField}>
                    <FormTextField
                      label="Truck Driver Number"
                      value={values?.soilFLOData?.truck?.driverPhone}
                    />
                  </div>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <div className={classes.soilFLOField}>
                    <FormTextField
                      label="Truck License Plate"
                      value={values?.soilFLOData?.truck?.global_truck?.license
                        ?.trim()
                        ?.replace(" ", "")}
                    />
                  </div>
                </Grid>
              </Grid>

              <div style={{ border: "1px solid #ccc", marginTop: 16, marginBottom: 16 }} />

              {/* United Soils Info */}
              <Typography variant="h6" gutterBottom>
                United Soils Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormSelect
                    name="userId"
                    label="Receiving Contact"
                    value={values.userId}
                    items={users.map((user, index) => (
                      <MenuItem key={index} value={user.id}>
                        {user.firstName} {user.lastName} ({user.phoneNumber})
                      </MenuItem>
                    ))}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormDateTimePicker
                    label="Time Received"
                    value={
                      values.timeReceived ? new Date(values.timeReceived) : null
                    }
                    onChange={(date: any) =>
                      setFieldValue("timeReceived", date)
                    }
                    clearable
                  />
                  <FormErrorMessage name="timeReceived" />
                </Grid>
              </Grid>

              {isAdmin(user.roleId) && (
                <Box display="flex" justifyContent="flex-end" mt={3}>
                  {!initialValues.isReceived && (
                    <Button
                      variant="contained"
                      type="submit"
                      disabled={saving}
                      style={{ marginRight: 8 }}
                    >
                      {initialValues.isReceived
                        ? "Reset Receive"
                        : "Receive Ticket"}
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={saving}
                    onClick={() => onSaveSoilFLOTicket(values)}
                  >
                    Save
                  </Button>
                </Box>
              )}
            </form>
          )}
        </Formik>
      </Paper>
    </Container>
  );
};

export default connect()(TicketSoilFLOView);
